import {
  faStar,
  faPlay,
  faFileCircleCheck,
  faComment,
  faCheck,
} from '@fortawesome/pro-regular-svg-icons';
import { StatusTagProps } from '@client/new/components/StatusTag/StatusTag';
import { format } from 'date-fns';
import { PATHS } from '../../../../routes';
import { DragTableProps } from '../../../components/DragTable/DragTable';
import {
  cellIconText,
  CellProps,
  CellSelectItem,
  CellSelectProps,
} from '../../../components/TableShared/Cell';
import { F1, TMap, Option } from '../../../helpers/types';
import {
  WorkoutsModel,
  LiteWorkout,
  WorkoutType,
  DayName,
  WeekName,
} from './workouts.domain';
import {
  ToSmartTablePropsParams,
  VMDispatcher,
  WorkoutsVMActionsMap,
} from './workouts.viewModel';
import { groupBy } from 'lodash';
import {
  Row,
  StandardTableProps,
} from '../../../components/StandardTable/StandardTable';
import { toArray } from '../../../helpers/general';
import { workoutTypeToTab } from '../domain';
import { extendDispatch } from '@client/new/helpers/types-utils';

const readonlySimpleTableSchema: DragTableProps<number>['schema'] = [
  { width: 'stretchable', label: 'Workout name' },
];

const simpleTableSchema: DragTableProps<number>['schema'] = [
  ...readonlySimpleTableSchema,
  { width: 150, label: '', justify: 'right' },
  { width: 50, label: 'action', justify: 'right' },
];

const readonlyFullTableSchema: DragTableProps<number>['schema'] = [
  { width: 'stretchable', label: 'Workout name' },
  { width: 120, label: 'Free sample' },
  { width: 100, label: 'rating' },
  { width: 100, label: 'begins' },
  { width: 100, label: 'completions' },
  { width: 100, label: 'comments' },
];

const fullTableSchema: DragTableProps<number>['schema'] = [
  ...readonlyFullTableSchema,
  { width: 50, label: 'actions', justify: 'right' },
];

type ToCellsMapperParams = Pick<
  WorkoutsModel,
  | 'influencerId'
  | 'workoutsStructure'
  | 'isProgramEditable'
  | 'readonly'
  | 'isWorkoutArchiveEnabled'
> & {
  dispatch: VMDispatcher;
  workout: LiteWorkout;
  selectedList: WorkoutType;
  visibleWorkoutIds?: ReadonlySet<number>;
};

const visibleTag: StatusTagProps = { type: 'ok', text: 'Visible' };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CellPropsArray = Array<CellProps<any>>;
type CellMapper = F1<ToCellsMapperParams, CellPropsArray>;

const getFormattedDate = (date: Date) => format(date, 'MMM d yyyy, h:mma');

const toDraftCells: CellMapper = ({
  workout,
  dispatch,
  ...p
}): CellPropsArray => {
  const { lazyDispatch } = extendDispatch<WorkoutsVMActionsMap>(dispatch);
  const subtitle = workout.lastEditedDate
    ? 'Last edited ' + getFormattedDate(new Date(workout.lastEditedDate))
    : '';
  const editURL = PATHS.WORKOUT_EDIT({
    ...workout,
    ...p,
    fromWorkoutTab: workoutTypeToTab[p.selectedList],
  });

  return <CellPropsArray>[
    { type: 'Head', value: { title: workout.orgTitle, subtitle } },
    ...(!p.readonly
      ? [
          {
            type: 'CTA items',
            value: {
              items: [
                {
                  type: 'Button',
                  value: {
                    title: 'Publish',
                    onClick: lazyDispatch('publish', workout),
                    color: 'primaryTonal',
                  },
                },
              ],
            },
          },
        ]
      : []),
    {
      type: 'Menu',
      value: {
        items: [
          {
            text: p.readonly ? 'Preview' : 'Edit',
            to: editURL,
          },
          {
            text: 'Duplicate',
            onClick: lazyDispatch('duplicate', workout),
          },
          {
            text: 'Delete',
            onClick: lazyDispatch('remove', workout),
          },
        ],
      },
    },
  ];
};

const toScheduledCells: CellMapper = ({
  workout,
  dispatch,
  ...p
}): CellPropsArray => {
  const { lazyDispatch } = extendDispatch<WorkoutsVMActionsMap>(dispatch);
  const subtitle = workout.scheduledDate
    ? 'Scheduled for ' + getFormattedDate(new Date(workout.scheduledDate))
    : '';
  const editURL = PATHS.WORKOUT_EDIT({
    ...workout,
    ...p,
    fromWorkoutTab: workoutTypeToTab[p.selectedList],
  });

  return <CellPropsArray>[
    {
      type: 'Head',
      value: { title: workout.orgTitle, subtitle },
    },
    {
      type: 'Menu',
      value: {
        items: [
          {
            text: p.readonly ? 'Preview' : 'Edit',
            to: editURL,
          },
          {
            text: 'Duplicate',
            onClick: lazyDispatch('duplicate', workout),
          },
          {
            text: 'Archive',
            onClick: lazyDispatch('archive', workout),
            disabled: !p.isProgramEditable && !p.isWorkoutArchiveEnabled,
            tooltip:
              p.isProgramEditable || p.isWorkoutArchiveEnabled
                ? 'Click here to store away workout for later reference, keeping your program organized'
                : 'Program is published and workouts can’t be archived. Please contact support if you need to proceed with this action',
          },
        ],
      },
    },
  ];
};

const toStatsCells = (workout: LiteWorkout): Array<CellProps> => [
  // A workout is a free sample when it is not protected behind the paywall.
  workout.freeSample ? cellIconText(undefined, faCheck) : cellIconText(''),
  cellIconText(workout.rating || '-', faStar),
  cellIconText(workout.begins, faPlay),
  cellIconText(workout.completions, faFileCircleCheck),
  cellIconText(workout.comments, faComment),
];

const emptyWeekOption: CellSelectItem<WeekName> = {
  label: 'W',
  value: 'W' as WeekName,
  disabled: true,
};

const emptyDayOption: CellSelectItem<DayName> = {
  label: 'D',
  value: 'D' as DayName,
  disabled: true,
};

const weekOptions: Option<WeekName>[] = [
  emptyWeekOption,
  ...toArray(20, (i): Option<WeekName> => ({
    label: `W ${i + 1}`,
    value: `W${i + 1}` as WeekName,
  })),
];

const dayOptions: Option<DayName>[] = [
  emptyDayOption,
  ...toArray(7, (i): Option<DayName> => ({
    label: `D ${i + 1}`,
    value: `D${i + 1}` as DayName,
  })),
];

const toPublishedCells: CellMapper = ({ workout, ...p }) => {
  const inSeries = p.workoutsStructure === 'series';
  const { dispatch, lazyDispatch } = extendDispatch<WorkoutsVMActionsMap>(
    p.dispatch,
  );
  const weekDaysCells = [
    {
      type: 'Select',
      value: {
        items: weekOptions,
        toValue: (v) => v as WeekName,
        fromValue: (v) => v as string,
        onChange: (newWeek) =>
          dispatch('setWeek', {
            workoutId: workout.workoutId,
            newWeek,
            day: workout.day,
          }),
        value: workout.week ?? emptyWeekOption.value,
      },
    } as CellSelectProps<WeekName>,
    {
      type: 'Select',
      value: {
        items: dayOptions,
        toValue: (v) => v as DayName,
        fromValue: (v) => v as string,
        onChange: (newDay) =>
          dispatch('setDay', {
            workoutId: workout.workoutId,
            newDay,
            week: workout.week,
          }),
        value: workout.day ?? emptyDayOption.value,
      },
    } as CellSelectProps<DayName>,
  ];

  const title = inSeries ? workout.orgTitle : workout.fixedTitle;
  const subtitle = workout.publishedDate
    ? 'Published ' + getFormattedDate(new Date(workout.publishedDate))
    : '';

  const titleCell: CellProps = {
    type: 'Head',
    value: {
      showOrder: inSeries,
      title,
      subtitle,
      tags: p.visibleWorkoutIds?.has(workout.workoutId)
        ? [visibleTag]
        : undefined,
    },
  };

  const editURL = PATHS.WORKOUT_EDIT({
    ...workout,
    ...p,
    fromWorkoutTab: workoutTypeToTab[p.selectedList],
  });
  const baseMenu: CellPropsArray = [
    inSeries
      ? titleCell
      : { type: 'Items', value: [...weekDaysCells, titleCell] },
    ...toStatsCells(workout),
  ];

  return [
    ...baseMenu,
    {
      type: 'Menu',
      value: {
        items: [
          {
            text: p.readonly ? 'Preview' : 'Edit',
            to: editURL,
          },
          {
            text: 'Copy link',
            onClick: lazyDispatch('openLinkModal', workout.workoutId),
          },
          {
            text: 'Duplicate',
            onClick: lazyDispatch('duplicate', workout),
          },
          {
            text: 'Archive',
            onClick: lazyDispatch('archive', workout),
            disabled: !p.isWorkoutArchiveEnabled && !p.isProgramEditable,
            tooltip:
              p.isProgramEditable || p.isWorkoutArchiveEnabled
                ? 'Click here to store away workout for later reference, keeping your program organized'
                : 'Program is published and workouts can’t be archived. Please contact support if you need to proceed with this action',
          },
        ],
      },
    },
  ];
};

const toArchivedCells: CellMapper = ({ workout, dispatch, ...p }) => {
  const { lazyDispatch } = extendDispatch<WorkoutsVMActionsMap>(dispatch);
  const subtitle = workout.archivedDate
    ? 'Archived ' + getFormattedDate(new Date(workout.archivedDate))
    : '';

  const editURL = PATHS.WORKOUT_EDIT({
    ...workout,
    ...p,
    fromWorkoutTab: workoutTypeToTab[p.selectedList],
  });
  const baseMenu: CellPropsArray = [
    { type: 'Head', value: { title: workout.orgTitle, subtitle } },
    ...toStatsCells(workout),
  ];

  return [
    ...baseMenu,
    {
      type: 'Menu',
      value: {
        items: [
          {
            text: p.readonly ? 'Preview' : 'Edit',
            to: editURL,
          },
          {
            text: 'Duplicate',
            onClick: lazyDispatch('duplicate', workout),
          },
        ],
      },
    },
  ];
};

const cellsMappers: TMap<WorkoutType, CellMapper> = {
  draft: toDraftCells,
  scheduled: toScheduledCells,
  published: toPublishedCells,
  archived: toArchivedCells,
};

// Sentinel ids for the non-workout separator rows. Negative so they can never
// collide with a real (positive) workout id used as the React row key.
const DIVIDER_ROW_ID = -1;
const toWeekTitleRowId = (week: number) => -1000 - week;

const dividerRow: Row<number> = {
  id: DIVIDER_ROW_ID,
  single: { type: 'DividerRow', value: {} },
  cells: [],
};

const toWeekTitleRow = (week: number): Row<number> => ({
  id: toWeekTitleRowId(week),
  single: { type: 'TitleRow', value: 'WEEK ' + week },
  cells: [],
});

const toGroupedByWeekRows = (p: ToSmartTablePropsParams) => {
  const byWeeks = groupBy(p.workouts, 'week');
  const notAssignedWeekName = undefined + '';
  const notAssignedWorkouts = byWeeks[notAssignedWeekName] || [];
  const sortedDays = (week: string) =>
    (byWeeks[week] || []).sort((l, r) => l.orgTitle.localeCompare(r.orgTitle));

  const rows = Object.keys(byWeeks)
    .sort((l, r) => l.localeCompare(r))
    .filter((w) => w !== notAssignedWeekName)
    .sort((l, r) => {
      const w1 = parseInt(l.replace(/^[Ww]/, ''), 10);
      const w2 = parseInt(r.replace(/^[Ww]/, ''), 10);
      return w1 === w2 ? 0 : w1 < w2 ? -1 : 1;
    })
    .reduce(
      (acc, w) => {
        const week = parseInt(w.replace(/^[Ww]/, ''), 10);
        const weekTitle = toWeekTitleRow(week);
        return [
          ...acc,
          weekTitle,
          ...toRows({ ...p, workouts: sortedDays(w) }),
        ];
      },
      [] as Array<Row<number>>,
    );

  return notAssignedWorkouts.length > 0
    ? [...rows, dividerRow, ...toRows({ ...p, workouts: notAssignedWorkouts })]
    : rows;
};

const toRows = (p: ToSmartTablePropsParams) => {
  return p.workouts.map((workout) => {
    const state = p.workoutsStateMap[workout.workoutId];
    const cells =
      state?.type === 'fetching'
        ? [{ type: 'Busy' as const, value: {} }]
        : cellsMappers[p.selectedList]({ workout, ...p });
    return { id: workout.workoutId, cells } as Row<number>;
  });
};

export const toStandardTableProps = (
  p: ToSmartTablePropsParams,
): StandardTableProps<number> => {
  const withStats =
    p.selectedList === 'published' || p.selectedList === 'archived';

  const schema = withStats ? fullTableSchema : simpleTableSchema;

  const rows =
    p.selectedList === 'published' && p.workoutsStructure === 'week/day'
      ? toGroupedByWeekRows(p)
      : toRows(p);
  return { schema, rows };
};
